import URI from 'jsuri';

import API from 'js/lib/api';
import user from 'js/lib/user';

import type { GoalNames } from 'bundles/enterprise-learner-onboarding/components/LearnerOnboardingContainer';
import type { LearnerLevels } from 'bundles/enterprise-learner-onboarding/components/LevelSelectionStep';
import {
  ONBOARDING_SURVEY_BASE_ID,
  ONBOARDING_SURVEY_VERSION,
  QUESTION_RESPONSE_TYPE_MAP,
} from 'bundles/enterprise-learner-onboarding/constants';
import type {
  SurveyCreateRequest,
  SurveyData,
  SurveyQuestion,
  SurveyResponse,
  SurveyResponseQuestion,
  SurveyUpdateRequest,
} from 'bundles/onboarding-2022/types/Survey';
import { QuestionTypes, ResponseTypes } from 'bundles/survey-form/constants/SurveyFormConstants';
import type { QuestionType } from 'bundles/survey-form/constants/SurveyFormConstants';

export const onboardingSurveyResponsesAPI = API('/api/surveySectionResponses.v1', { type: 'rest' });

export const SurveyQuestionIds = {
  goal: 'learningGoal',
  role: 'learningRole',
  unsure: 'roleUnsure',
  level: 'learnerLevel',
};

export const formatQuestions = (questions: SurveyResponseQuestion[]): SurveyQuestion[] => {
  return questions.map((question) => {
    const questionType = Object.keys(question.content)[0] as QuestionType;
    const content = question.content[questionType];
    // short answer is freeform - disregard the BE response for the options in this case
    const options = questionType === QuestionTypes.SHORT_ANSWER ? [] : content.options;
    return {
      type: questionType,
      prompt: content.prompt,
      options,
      id: question.id,
    };
  });
};

export const formatSurveyResponse = (response: SurveyResponse): SurveyData => {
  if (!response || response.elements.length === 0) {
    throw new Error('No survey response available');
  }

  const data = response.elements[0];
  const { responseId, sectionId, section } = data;
  const { isTerminal, title } = section;

  if (isTerminal) {
    return {
      responseId,
      sectionId,
      isTerminal,
    };
  }

  return {
    responseId,
    sectionId,
    isTerminal,
    title,
    questions: formatQuestions(section.questions),
  };
};

export const createSurvey = (): Promise<SurveyData> => {
  const payload: SurveyCreateRequest = {
    surveyId: {
      baseId: ONBOARDING_SURVEY_BASE_ID,
      version: ONBOARDING_SURVEY_VERSION,
    },
    scope: {
      identity: {
        'org.coursera.survey.scope.LoggedInIdentity': {
          userId: user.get()?.id,
        },
      },
      context: {
        'org.coursera.survey.scope.NoContext': {},
      },
    },
  };
  return new Promise((resolve, reject) => {
    const uri = new URI().addQueryParam('fields', 'section,sectionId');
    onboardingSurveyResponsesAPI
      .post(uri.toString(), { data: payload })
      .then(formatSurveyResponse)
      .then(resolve)
      .fail(reject);
  });
};

export const updateSurvey = ({
  questions,
  sectionId,
  responseId,
  selectedGoals,
  roleInputText,
  isUnsureRole,
  selectedLevel,
}: {
  questions: SurveyQuestion[];
  sectionId: string;
  responseId: string;
  selectedGoals: GoalNames[];
  roleInputText: string;
  selectedLevel?: LearnerLevels;
  isUnsureRole: boolean;
}): Promise<SurveyData> => {
  const surveyResponseMap = questions.reduce((acc, currentQuestion) => {
    const responseType = QUESTION_RESPONSE_TYPE_MAP[currentQuestion.type];
    let answers;
    if (responseType === ResponseTypes.CHECKBOX_RESPONSE) {
      answers = {
        ids: selectedGoals,
      };
    } else if (responseType === ResponseTypes.SHORT_ANSWER_RESPONSE) {
      answers = {
        text: roleInputText,
      };
    } else if (responseType === ResponseTypes.MULTIPLE_CHOICE_RESPONSE) {
      if (currentQuestion.id === SurveyQuestionIds.unsure) {
        answers = {
          id: String(isUnsureRole),
        };
      } else if (currentQuestion.id === SurveyQuestionIds.level) {
        if (!selectedLevel) {
          return acc;
        }

        answers = {
          id: selectedLevel,
        };
      }
    }

    return {
      ...acc,
      [currentQuestion.id]: {
        [responseType]: answers,
      },
    };
  }, {});

  const payload: SurveyUpdateRequest = {
    sectionId,
    responses: surveyResponseMap,
  };

  return new Promise((resolve, reject) => {
    const uri = new URI(responseId).addQueryParam('fields', 'section,sectionId');
    return onboardingSurveyResponsesAPI.put(uri.toString(), { data: payload }).then(resolve).fail(reject);
  });
};
